import { FormEvent, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { areBothDatesPresent, formatDate, isOneDatePresent } from '../../../utils/helpers';
import { useGetAllServices } from '../../../apis/services/get-all-services';
import { useCancelService } from '../../../apis/services/cancel-service';

const useServicesDashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const form = useRef<HTMLFormElement>(null);

    const [isCreatingService, setIsCreatingService] = useState(false);
    const [selectedServiceId, setSelectedServiceId] = useState("");
    const [selectedFeedback, setSelectedFeedback] = useState<{
        orderId: string;
        experienceRating: null | number;
        feedback: string;
    }>({ orderId: "", experienceRating: null, feedback: "" });

    const [status, setStatus] = useState(searchParams.get("status") || "");
    const [type, setType] = useState(searchParams.get("type") || "");
    const [fromDate, setFromDate] = useState<string | null>(searchParams.get("fromDate") || "");
    const [toDate, setToDate] = useState<string | null>(searchParams.get("toDate") || "");

    const { data, isFetching: loading, refetch } = useGetAllServices({
        limit: 10,
        fromdate: searchParams.get("fromDate") || "",
        todate: searchParams.get("toDate") || "",
        status: searchParams.get("status") || "",
        page: parseInt(searchParams.get("page") || "1"),
        type: searchParams.get("type") || "",
    });

    const { mutateAsync: cancelService, isPending: isCancellingService } = useCancelService();

    const handleServiceCancel = async () => {
        try {
            await cancelService(selectedServiceId);
            toast.success("Service Cancelled Successfully");
            refetch();
        } catch (error) {
            toast.error("Couldn't cancel service at this moment. Please try again later.");
        } finally {
            setSelectedServiceId("");
        }
    };

    const filterServices = (e: FormEvent) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        const newParams = new URLSearchParams();

        for (const [key, value] of formData.entries()) {
            if (value) newParams.append(key, value.toString());
        }

        if (fromDate) newParams.set("fromDate", fromDate);
        if (toDate) newParams.set("toDate", toDate);
        newParams.set("page", "1");
        navigate(`?${newParams.toString()}`);
    };

    const handlePageClick = (e: { selected: number }) => {
        searchParams.set("page", (e.selected + 1).toString());
        navigate(`?${searchParams.toString()}`);
    };

    const resetFilters = () => {
        setStatus("");
        setType("");
        setFromDate("");
        setToDate("");
        form.current?.reset();
        navigate("");
    };

    const handleFromDate = (e: Date | null | undefined) =>
        setFromDate(formatDate(e?.toString() || ""));

    const handleToDate = (e: Date | null | undefined) =>
        setToDate(formatDate(e?.toString() || ""));

    const isAnyFilterApplied = () => status !== "" || type !== "";

    const isApplyButtonDisabled = () => {
        if (isAnyFilterApplied()) return isOneDatePresent(fromDate, toDate);
        return !areBothDatesPresent(fromDate, toDate);
    };

    const isClearButtonDisabled = () =>
        status === "" && type === "" && fromDate === "" && toDate === "";

    return {
        form,
        services: data?.docs ?? [],
        totalPage: data?.totalPages ?? 1,
        loading,
        isCreatingService,
        setIsCreatingService,
        selectedServiceId,
        setSelectedServiceId,
        selectedFeedback,
        setSelectedFeedback,
        isCancellingService,
        status,
        setStatus,
        type,
        setType,
        fromDate,
        toDate,
        handleFromDate,
        handleToDate,
        filterServices,
        handlePageClick,
        handleServiceCancel,
        resetFilters,
        isApplyButtonDisabled,
        isClearButtonDisabled,
        searchParams,
        refetch,
    };
};

export default useServicesDashboard;